import axios from 'axios';
import { getCookie, setCookie, removeCookie } from './cookie';

//~ Axios 인스턴스 (토큰 자동 주입 + 만료 시 자동 재발급/로그아웃)
const api = axios.create();

api.interceptors.request.use(config => {
  const token = getCookie('adminMyToken');
  if (token) config.headers['access-token'] = token;
  if (!config.headers['Content-Type'])
    config.headers['Content-Type'] = 'application/json';
  return config;
});

const fileHeader = () => ({
  headers: { 'Content-Type': 'multipart/form-data' },
});

//~ 강제 로그아웃 (refresh 토큰까지 만료된 경우)
const performLogout = () => {
  removeCookie('adminMyToken');
  removeCookie('adminRfToken');
  localStorage.removeItem('admin_role');
  if (window.location.pathname !== '/') {
    window.location.replace('/');
  }
};

//~ 토큰 재발급 (단일 비행 보장: 동시 401 들이 한 번만 재발급 호출)
let reissuePromise = null;
const tokenReissue = () => {
  if (reissuePromise) return reissuePromise;
  reissuePromise = (async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'access-token': getCookie('adminMyToken'),
        'refresh-token': getCookie('adminRfToken'),
      };
      const result = await axios.get(`/api/users/self/token`, { headers });
      removeCookie('adminMyToken');
      setCookie('adminMyToken', result.data.data.access_token, { path: '/' });
      return true;
    } catch {
      performLogout();
      return false;
    } finally {
      setTimeout(() => {
        reissuePromise = null;
      }, 0);
    }
  })();
  return reissuePromise;
};

api.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error?.config || {};
    const { status } = error?.response || {};
    const responseData = error?.response?.data || {};
    const errorCode = responseData?.error?.code;
    const errorDetail = responseData?.error?.detail || responseData?.detail;

    const isAccessExpired =
      errorDetail === 'AccessTokenExpired' &&
      (status === 403 ||
        errorCode === 'TOKEN_EXPIRED' ||
        errorCode === 'INVALID_TOKEN');

    if (isAccessExpired && !originalRequest._retry) {
      originalRequest._retry = true;
      const ok = await tokenReissue();
      if (ok) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['access-token'] = getCookie('adminMyToken');
        return api(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

//~ API Error Handling Function
const errorHandling = async error => {
  const { status } = error?.response || {};
  const responseData = error?.response?.data || {};
  const errorCode = responseData?.error?.code;
  const errorDetail = responseData?.error?.detail || responseData?.detail;
  const detail = errorDetail;
  const failCount = detail?.split(',')[1];

  // 토큰 만료는 인터셉터에서 재발급/로그아웃 처리.
  // 여기까지 도달했다면 재발급도 실패한 케이스이므로 'tokenExpired' 신호.
  if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN') {
    return 'tokenExpired';
  }

  // 미매칭 상황에서 사용자에게 보여줄 fallback 메시지
  // (백엔드 도메인 에러: error.message > message > error.detail > detail)
  const domainMessage =
    responseData?.error?.message ||
    responseData?.message ||
    errorDetail;

  switch (status) {
    case 401:
      if (detail === 'Access Denied') return 'accessDenied';
      else if (detail === 'Please Add Days') return 'addDays';
      else if (detail === 'Keyword Too Long') return 'keywordTooLong';
      break;
    case 403:
      if (detail === 'LoginRequired') return 'tokenExpired';
      else if (detail === 'DuplicateLoginDetection') return 'duplicateLogin';
      else if (detail === 'Invaild User ID') return 'wrongId';
      else if (detail === `Invaild Password,${failCount}`)
        return `wrongPw,${failCount}`;
      else if (detail === 'Days Limit Exceeded') return 'datePasses';
      else if (detail === 'Expiration Date Exceeds limit')
        return 'exceedCharOfDate';
      else if (detail === 'Duplicated Id') return 'duplicateId';
      else if (detail === 'Retired User') return 'retired';
      else if (detail === 'Not admin') return 'retired';
      break;
    case 499:
      return 'tokenError';
    case 500:
      return 'serverError';
    case 404:
      return 'notFound';
    case 501:
      if (detail === 'IntegrityError') return 'integrityError';
      break;
    case 504:
      return 'renderErrorPage';
    default:
      break;
  }

  // 알려진 키에 매칭되지 않은 도메인 에러는 백엔드 메시지를 그대로 노출
  if (domainMessage) return domainMessage;
  return undefined;
};

//~ LogIn
export const signIn = async (userId, userPw) => {
  try {
    return await api.post('/api/admin/login', {
      user_id: userId,
      password: userPw,
    });
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ Home
// 회원 Count
export const getUserCount = async target => {
  try {
    return await api.get(`/api/admin/users/count?target=${target}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 검색 Count
export const getSearchCount = async target => {
  try {
    return await api.get(`/api/admin/search/count?target=${target}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 검색왕 순위
export const getSearchKing = async target => {
  try {
    return await api.get(`/api/admin/search/king?target=${target}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ Manage
// 회원 정보 조회
export const getUserList = async (page, limit) => {
  try {
    return await api.get(`/api/admin/users?page=${page}&limit=${limit}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 회원 정보 검색
export const searchUser = async (target, search_word, page, limit) => {
  try {
    return await api.get(
      `/api/admin/users/search?target=${target}&search_word=${search_word}&page=${page}&limit=${limit}`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 쿠폰 발급
export const applyToken = async data => {
  try {
    return await api.post(`/api/admin/service/apply/token`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 쿠폰 직접 발금
export const applyNewCoupon = async couponInfo => {
  try {
    return await api.post(`/api/admin/service/issue/coupon`, couponInfo);
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ Inquiry
// 문의 내역 조회
export const getInquiryList = async (target, page, limit) => {
  try {
    return await api.get(
      `api/admin/inquiry?filter_type=${target}&page=${page}&limit=${limit}`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 문의 내역 상세 페이지
export const getInquiryDetail = async id => {
  try {
    return await api.get(`/api/admin/inquiry/${id}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 답변 등록
export const answerPost = async (id, data) => {
  try {
    return await api.post(`/api/admin/inquiry/answer/${id}`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 답변 수정
export const answerEdit = async (id, data) => {
  try {
    return await api.post(`/api/admin/inquiry/answer-edit/${id}`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 답변 삭제
export const answerDelete = async id => {
  try {
    return await api.post(`/api/admin/inquiry/answer-delete/${id}`, {});
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ Notice
// 공지 사항 내역 불러오기
export const getNoticeList = async (page, limit) => {
  try {
    return await api.get(
      `/api/admin/notification?page=${page}&limit=${limit}`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 공지 사항 상세 페이지
export const getNoticeDetail = async id => {
  try {
    return await api.get(`/api/admin/notification/${id}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

//공지 사항 작성
export const noticeWrite = async formData => {
  try {
    return await api.post(`/api/admin/notification`, formData, fileHeader());
  } catch (error) {
    return await errorHandling(error);
  }
};

// 공지 사항 수정
export const noticeEdit = async (id, formData) => {
  try {
    return await api.post(
      `/api/admin/notification/edit/${id}`,
      formData,
      fileHeader()
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 공지 사항 삭제
export const noticeDelete = async id => {
  try {
    return await api.post(`/api/admin/notification/delete/${id}`, {});
  } catch (error) {
    return await errorHandling(error);
  }
};

// 공지 사항 다중 삭제
export const noticeMultiDelete = async data => {
  try {
    return await api.post(`/api/admin/notification/delete`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ Event
// 쿠폰 발급 내역 조회
export const getCouponList = async (page, limit) => {
  try {
    return await api.get(
      `/api/admin/service/view/token?page=${page}&limit=${limit}`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ USA Data Cache Management
// 캐시 불러오기
export const getCacheList = async () => {
  try {
    return await api.get('/us/system/dump_cache?passcode=kingsan');
  } catch (error) {
    return await errorHandling(error);
  }
};

// 캐시 사이즈 조회
export const getCacheSize = async () => {
  try {
    return await api.get('/us/system/get_cache_size?passcode=kingsan');
  } catch (error) {
    return await errorHandling(error);
  }
};

// 캐시 지우기
export const removeCache = async () => {
  try {
    return await api.get('/us/system/clear_cache?passcode=kingsan');
  } catch (error) {
    return await errorHandling(error);
  }
};

// 캐시 JSON 지우기
export const removeCacheJson = async () => {
  try {
    return await api.get('/us/system/manual_removal_json?passcode=kingsan');
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 서비스 관리
// 서비스 목록 불러오기
export const getServices = async () => {
  try {
    return await api.get(`/api/admin/service`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 서비스 생성
export const createService = async data => {
  try {
    return await api.post('/api/admin/service', data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 서비스 수정
export const editService = async (code, data) => {
  try {
    return await api.post(`/api/admin/service/edit/${code}`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

//서비스 삭제
export const deleteService = async code => {
  try {
    return await api.post(`/api/admin/service/delete/${code}`, {});
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 상품 관리
// 상품 불러오기
export const getMerchant = async () => {
  try {
    return await api.get(`/api/admin/merchants`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 상품 등록
export const applyMerchant = async data => {
  try {
    return await api.post(`/api/admin/merchants`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 상품 수정
export const editMerchant = async (id, data) => {
  try {
    return await api.post(`/api/admin/merchants/edit/${id}`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 상품 다중 삭제
export const productMultiDelete = async arr => {
  try {
    return await api.post(`/api/admin/merchants/delete`, arr);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 상품 삭제
export const deleteMerchant = async id => {
  try {
    return await api.post(`/api/admin/merchants/delete/${id}`, {});
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 관리자 관리
// 아이디 중복체크
export const idDuplicateCheck = async id => {
  try {
    return await api.post(`/api/admin/check/id-duplicate`, { user_id: id });
  } catch (error) {
    return await errorHandling(error);
  }
};

// 관리자 목록 불러오기
export const getAdmin = async ({ page, limit }, filter, searchInput) => {
  try {
    return await api.get(
      `/api/admin/accounts?page=${page}&limit=${limit}${
        filter === 'all'
          ? ''
          : `&filter_type=${filter}&filter_val=${searchInput}`
      }`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 관리자 추가
export const addAdmin = async data => {
  try {
    return await api.post(`/api/admin/accounts`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 관리자 다중 삭제
export const adminMultiDelete = async pkArr => {
  try {
    const query = {
      items: pkArr,
    };
    return await api.post(`/api/admin/accounts/delete`, query);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 관리자 삭제
export const adminDelete = async pk => {
  try {
    return await api.post(`/api/admin/accounts/delete/${pk}`, {});
  } catch (error) {
    return await errorHandling(error);
  }
};

// 관리자 수정
export const adminEdit = async ({
  pk,
  password,
  admin_role,
  birthday,
  phone,
  email,
}) => {
  try {
    const query = {
      password: password === 'samplePw' ? null : password,
      admin_role: admin_role,
      birthday: birthday,
      phone: phone.replaceAll('-', ''),
      email: email,
    };
    return await api.post(`/api/admin/accounts/edit/${pk}`, query);
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 팝업 관리
// 팝업 불러오기
export const getPopUpList = async (due, { page, limit }) => {
  try {
    return await api.get(
      `/api/admin/popup?filter_type=${due}&page=${page}&limit=${limit}`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 팝업 등록
export const createPopup = async ({ service_code, start, end, img, link }) => {
  try {
    return await api.post(
      `/api/admin/popup?service_code=${service_code}&start_date=${start}&end_date=${end}${
        link.trim() === '' ? '' : `&link_url=${link}`
      }`,
      { file: img },
      fileHeader()
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 팝업 수정
export const editPopup = async ({
  id,
  service_code,
  start,
  end,
  img,
  link,
}) => {
  try {
    return await api.post(
      `/api/admin/popup/edit/${id}?service_code=${service_code}&start_date=${start}&end_date=${end}${
        link.trim() === '' ? '' : `&link_url=${link}`
      }`,
      typeof img === 'string' ? {} : { file: img },
      typeof img === 'string' ? undefined : fileHeader()
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 팝업 삭제
export const deletePopup = async id => {
  try {
    return await api.post(`/api/admin/popup/delete/${id}`, {});
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 상표 QNA
// QNA 불러오기
export const getQna = async ({ page, limit }) => {
  try {
    return await api.get(`/api/general/qna?page=${page}&limit=${limit}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// QNA Detail 불러오기
export const getQnaDetail = async id => {
  try {
    return await api.get(`/api/general/qna/${id}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// QNA 등록
export const newQna = async postInfo => {
  try {
    return await api.post(`/api/admin/qna`, postInfo);
  } catch (error) {
    return await errorHandling(error);
  }
};

// QNA 삭제
export const deleteQna = async idArr => {
  try {
    return await api.post(`/api/admin/qna/delete`, { items: idArr });
  } catch (error) {
    return await errorHandling(error);
  }
};

// QNA 수정
export const editQna = async (id, postInfo) => {
  try {
    return await api.post(`/api/admin/qna/edit/${id}`, postInfo);
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 쿠폰/이용권 관리
// 이용권 목록 조회 (회원정보 포함)
export const getSubscriptions = async ({ page, limit, user_pk, type, status }) => {
  try {
    let url = `/api/admin/coupon-management/subscriptions?page=${page}&limit=${limit}`;
    if (user_pk) url += `&user_pk=${user_pk}`;
    if (type && type !== 'all') url += `&type=${type}`;
    if (status && status !== 'all') url += `&status=${status}`;
    return await api.get(url);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 회원 검색 (이용권 조회용)
export const searchSubscriptionUser = async (target, keyword, page, limit) => {
  try {
    return await api.get(
      `/api/admin/coupon-management/users/search?target=${target}&keyword=${keyword}&page=${page}&limit=${limit}`
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 이용권 연장
export const extendSubscription = async (user_pk, duration_days) => {
  try {
    return await api.post(
      `/api/admin/coupon-management/subscriptions/extend`,
      { user_pk, duration_days }
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

// 이용권 신규 발급
export const createSubscriptions = async (user_pks, duration_days) => {
  try {
    return await api.post(
      `/api/admin/coupon-management/subscriptions`,
      { user_pks, duration_days }
    );
  } catch (error) {
    return await errorHandling(error);
  }
};

//~ 추가결제(Admin Order) 관리
// 결제건 목록
export const getAdminOrders = async ({ page, limit, user_pk, status }) => {
  try {
    let url = `/api/admin/admin-orders?page=${page}&limit=${limit}`;
    if (user_pk) url += `&user_pk=${user_pk}`;
    if (status && status !== 'all') url += `&status=${status}`;
    return await api.get(url);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 결제건 상세
export const getAdminOrderDetail = async merchant_uid => {
  try {
    return await api.get(`/api/admin/admin-orders/${merchant_uid}`);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 결제건 발행
export const createAdminOrder = async data => {
  try {
    return await api.post(`/api/admin/admin-orders`, data);
  } catch (error) {
    return await errorHandling(error);
  }
};

// 결제건 발행 취소 (Pending → Cancelled)
export const cancelAdminOrder = async (merchant_uid, reason) => {
  try {
    return await api.delete(`/api/admin/admin-orders/${merchant_uid}`, {
      data: reason ? { reason } : {},
    });
  } catch (error) {
    return await errorHandling(error);
  }
};

// 결제건 환불 (Paid → Refunded)
export const refundAdminOrder = async (merchant_uid, { amount, reason }) => {
  try {
    const body = { reason };
    if (amount) body.amount = amount;
    return await api.post(
      `/api/admin/admin-orders/${merchant_uid}/refund`,
      body
    );
  } catch (error) {
    return await errorHandling(error);
  }
};